import { useEffect, useRef, useState } from "react";
import {
  api,
  type CompanyHit,
  type Watchlist,
  type WatchlistsResponse,
} from "../lib/api";
import { SegToggle } from "../components/IntervalToggle";
import { relativeTime } from "../lib/time";
// The watch taxonomy (labels, per-source kinds, register metadata) is GENERATED
// from schemas/events.yaml — the SAME module the worker imports, so the UI
// can't drift from what the worker enforces. Widened to string-keyed records
// for the by-register lookups below.
import {
  EVENT_DESCRIPTIONS as GEN_EVENT_DESCRIPTIONS,
  EVENT_GROUPS as GEN_EVENT_GROUPS,
  EVENT_LABELS as GEN_EVENT_LABELS,
  REGISTERS,
  SOURCE_EVENTS as GEN_SOURCE_EVENTS,
} from "../../../../schemas/generated/ts/watch_events";

const EVENT_LABELS: Record<string, string> = GEN_EVENT_LABELS;
const EVENT_DESCRIPTIONS: Record<string, string> = GEN_EVENT_DESCRIPTIONS;
const SOURCE_EVENTS: Record<string, readonly string[]> = GEN_SOURCE_EVENTS;
const EVENT_GROUPS: readonly {
  id: string;
  label: string;
  types: readonly string[];
}[] = GEN_EVENT_GROUPS;

function deliveryLabel(w: Watchlist): string {
  if (w.notify_email)
    return `Email · ${w.email_cadence === "daily" ? "daily digest" : "digest + prompt"}`;
  if (w.webhook_url) return "Webhook";
  return "Poll-only";
}

// Registries with an event source behind them, derived from the generated
// REGISTERS metadata. [prefix, label, example local id] — the "search by id"
// selector and the watchable check below use this shape.
const SOURCES = REGISTERS.map(
  (r) => [r.id, r.label, r.exampleLocalId] as const,
);

function isWatchable(companyId: string): boolean {
  return SOURCES.some(([prefix]) => companyId.startsWith(prefix + "-"));
}

// The register prefix backing a company id (or null) — drives which events
// are offered for it.
function registerOf(companyId: string): string | null {
  return SOURCES.find(([p]) => companyId.startsWith(p + "-"))?.[0] ?? null;
}

// Stable identity for a search hit. The upstream groups by
// (entity_id, schema, jurisdiction), so entity_id alone is NOT unique
// across rows — using it as the React key (or selection key) would
// collide. `|` can't appear in any of these fields.
function hitKey(h: CompanyHit): string {
  return `${h.entity_id}|${h.schema}|${h.jurisdiction ?? ""}`;
}

type SearchMode = "id" | "name";

export function Watchlists() {
  const [data, setData] = useState<WatchlistsResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Company-selection state. Two ways in: by exact id (source selector +
  // local registry number) or by fuzzy name across all registries.
  const [mode, setMode] = useState<SearchMode>("name");
  // By-id mode.
  const [source, setSource] = useState<string>(REGISTERS[0].id);
  const [localId, setLocalId] = useState("");
  // Optional operator-supplied display label (by-id mode only; name mode
  // takes the label from the chosen search hit).
  const [displayName, setDisplayName] = useState("");
  // By-name mode.
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CompanyHit[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState(false);
  const [selected, setSelected] = useState<CompanyHit | null>(null);
  // The query string the current `results` are for — lets the UI show a
  // "press Enter to search" prompt once the field has diverged from it.
  const [searchedQuery, setSearchedQuery] = useState<string | null>(null);
  // Bumped on every search (and on reset); a late response only applies if
  // its seq still matches, so an out-of-order or superseded reply can't
  // clobber the list.
  const searchSeq = useRef(0);

  // Rest of the create form.
  const [kinds, setKinds] = useState<string[]>([]);
  const [emailOn, setEmailOn] = useState(false);
  const [email, setEmail] = useState("");
  const [cadence, setCadence] = useState<"daily" | "prompt_and_daily">(
    "prompt_and_daily",
  );
  const [busy, setBusy] = useState(false);

  // Effective company id + display name the submit uses, derived from the
  // active mode so there is one source of truth (no syncing effects). The
  // worker canonicalises case/zero-padding, so we send the raw join.
  const example = SOURCES.find(([p]) => p === source)?.[2] ?? "";
  const companyId =
    mode === "id"
      ? localId.trim()
        ? `${source}-${localId.trim()}`
        : ""
      : selected?.entity_id ?? "";
  // Trim before sending: a name-mode label comes from the upstream
  // matched_name (which we don't control), and an id-mode label is raw
  // operator input — the worker's company_name schema doesn't trim.
  const companyName =
    mode === "name"
      ? (selected?.matched_name ?? "").trim()
      : displayName.trim();
  const selectionWatchable = companyId !== "" && isWatchable(companyId);
  // The register backing the current selection drives which events are on
  // offer (each source fires a different subset). In id mode that's the chosen
  // source (so events show before a number is typed); in name mode it's the
  // selected hit's register, or null until one is picked.
  const activeRegister =
    mode === "id" ? source : companyId ? registerOf(companyId) : null;
  const availableEvents = activeRegister
    ? SOURCE_EVENTS[activeRegister] ?? []
    : [];
  // Coverage caveat for the active register, from the taxonomy (kept
  // consistent with SOURCE_EVENTS at the source, not here). find() already
  // yields undefined for a null activeRegister, so no extra guard.
  const registerNote = REGISTERS.find((r) => r.id === activeRegister)?.note;
  // The thematic display groups, cut down to what the active register
  // actually fires — a group none of whose kinds apply doesn't render.
  const eventGroups = EVENT_GROUPS.map((g) => ({
    ...g,
    types: g.types.filter((t) => availableEvents.includes(t)),
  })).filter((g) => g.types.length > 0);
  const canSubmit =
    !busy && kinds.length > 0 && companyId !== "" && selectionWatchable;
  // kinds is always a subset of availableEvents (the reset below drops
  // non-members; every mutation only adds offered kinds), so equal length
  // means all-selected — === keeps the label honest and surfaces any future
  // subset-invariant break instead of masking it.
  const allSelected = kinds.length === availableEvents.length;

  // Changing the source resets the event selection to everything that source
  // fires (the sensible default — watch all of it) and drops any kinds the new
  // source doesn't emit, so we never submit an event the worker would reject.
  // Done during render (not an effect) so no frame ever shows the previous
  // register's selection counted against the new register's list. The
  // undefined sentinel (activeRegister is string | null, never undefined) makes
  // the reset fire on the initial render too, so the watch-all default holds
  // even if the form ever mounts with a register already active.
  const [kindsRegister, setKindsRegister] = useState<string | null | undefined>(
    undefined,
  );
  if (kindsRegister !== activeRegister) {
    setKindsRegister(activeRegister);
    setKinds([...availableEvents]);
  }

  const refresh = () =>
    api
      .listWatchlists()
      .then((r) => {
        setData(r);
        // Default the recipient to the first membership email once known.
        const first = r.member_emails[0];
        if (first) setEmail((e) => e || first);
      })
      .catch((e) => setErr(String((e as Error).message)));

  useEffect(() => {
    refresh();
  }, []);

  // Cancel any in-flight search on unmount so a late response can't setState
  // on an unmounted component (the seq guard makes the stale reply a no-op).
  useEffect(
    () => () => {
      searchSeq.current++;
    },
    [],
  );

  // Explicit fuzzy search — run on Enter or the Search button, NOT per
  // keystroke: the upstream can take a few seconds, so as-you-type fired
  // (and paid for) a request on every typing pause and restarted the
  // spinner under the user. One search per intent instead.
  async function runSearch() {
    const q = query.trim();
    if (q.length < 2 || searching) return;
    const seq = ++searchSeq.current;
    setSearching(true);
    setSearchErr(false);
    setSearchedQuery(q);
    try {
      const r = await api.searchCompanies(q);
      if (seq === searchSeq.current) setResults(r.hits);
    } catch {
      if (seq === searchSeq.current) {
        setResults([]);
        setSearchErr(true);
      }
    } finally {
      if (seq === searchSeq.current) setSearching(false);
    }
  }

  // The one owner of kind-set membership: on=true unions the types in,
  // on=false drops them. Pure over a given array so both the bulk setter and
  // the per-pill toggle can apply it inside a functional updater — never
  // against a stale render-time closure.
  function applyKinds(
    ks: string[],
    types: readonly string[],
    on: boolean,
  ): string[] {
    return on
      ? [...ks, ...types.filter((t) => !ks.includes(t))]
      : ks.filter((k) => !types.includes(k));
  }

  // Bulk set for the All/None + Select-all/Clear-all controls: an explicit
  // desired end state, so `on` is known at the call site.
  function setKindsFor(types: readonly string[], on: boolean) {
    setKinds((ks) => applyKinds(ks, types, on));
  }

  // Per-pill toggle: read-modify-write against the LATEST state, so two
  // toggles of one kind batched into a render can't drop the second update.
  function toggleKind(k: string) {
    setKinds((ks) => applyKinds(ks, [k], !ks.includes(k)));
  }

  function resetSelection() {
    searchSeq.current++; // cancel any in-flight search
    setLocalId("");
    setDisplayName("");
    setQuery("");
    setResults(null);
    setSelected(null);
    setSearchErr(false);
    setSearching(false);
    setSearchedQuery(null);
  }

  async function create(e: React.FormEvent) {
    e.preventDefault();
    // Guard the submit, not just the button: a disabled button does NOT
    // block Enter-key form submission, so without this an empty or
    // non-watchable selection (e.g. whitespace-only id + Enter) would
    // POST a garbage company_id for the worker to reject.
    if (!canSubmit) return;
    setErr(null);
    setBusy(true);
    try {
      await api.createWatchlist({
        company_id: companyId,
        ...(companyName ? { company_name: companyName } : {}),
        event_types: kinds,
        ...(emailOn && email
          ? { notify_email: email, email_cadence: cadence }
          : {}),
      });
      resetSelection();
      refresh();
    } catch (ex) {
      setErr(String((ex as Error).message));
    } finally {
      setBusy(false);
    }
  }

  const emailAllowed = data?.email_delivery ?? false;

  return (
    <div className="main">
      <div className="page-header">
        <h1>Watchlists</h1>
        {data && (
          <span className="badge">
            Tracking {data.watchlists.length}
            {data.watch_limit > 0 ? ` of ${data.watch_limit}` : ""} companies
          </span>
        )}
      </div>
      <p className="muted">
        Watch a company and get notified about its financial events —
        accounts filed, charges, share allotments, status changes,
        insolvency. The events available, and how quickly they arrive,
        depend on the source (shown when you pick a company). Email
        delivery sends a daily digest, with significant events (insolvency,
        status change) sent promptly.
      </p>

      {err && <p className="error">{err}</p>}

      <div className="card">
        <h2>Watch a company</h2>
        <form onSubmit={create}>
          <div className="form-section">
            <div className="field-label">Company</div>
            {/* Mode toggle: find a company by name, or enter its id directly. */}
            <div className="seg" role="tablist" aria-label="Find a company by">
              <SegToggle
                value={mode}
                onChange={(m) => {
                  setMode(m);
                  resetSelection();
                }}
                options={[
                  { value: "name", label: "Search by name" },
                  { value: "id", label: "Enter id" },
                ]}
              />
            </div>

            {mode === "name" ? (
              <div style={{ marginTop: "var(--space-3)" }}>
                <div className="search-row">
                  <input
                    className="input"
                    placeholder="Company name (e.g. Acme Holdings)"
                    value={query}
                    aria-busy={searching}
                    onChange={(e) => {
                      const v = e.target.value;
                      setQuery(v);
                      setSelected(null);
                      // Any edit makes the field the single source of truth:
                      // cancel an in-flight search (seq bump, so its late
                      // response can't land) and drop the now-stale results /
                      // error / spinner — unless the trimmed value still
                      // matches what the current results are for (e.g. only
                      // whitespace changed).
                      if (v.trim() !== searchedQuery) {
                        searchSeq.current++;
                        setSearching(false);
                        setSearchErr(false);
                        setResults(null);
                        setSearchedQuery(null);
                      }
                    }}
                    onKeyDown={(e) => {
                      // Enter searches — and must NOT bubble to submit the
                      // outer create-watchlist form.
                      if (e.key === "Enter") {
                        e.preventDefault();
                        runSearch();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn secondary"
                    onClick={runSearch}
                    disabled={query.trim().length < 2 || searching}
                  >
                    {searching ? (
                      <span className="btn-busy">
                        <span className="spinner" aria-hidden /> Searching…
                      </span>
                    ) : (
                      "Search"
                    )}
                  </button>
                </div>

                {/* Search is explicit now — prompt to submit once the field
                    holds something searchable that hasn't been searched yet. */}
                {!searching &&
                  query.trim().length >= 2 &&
                  query.trim() !== searchedQuery && (
                    <p className="muted" style={{ marginTop: "var(--space-2)" }}>
                      Press Enter or Search to look up “{query.trim()}”.
                    </p>
                  )}

                {/* Spoken counterpart to the spinner — announces the async
                    search state to assistive tech. */}
                <span className="sr-only" role="status" aria-live="polite">
                  {searching
                    ? "Searching…"
                    : searchErr
                      ? "Search unavailable"
                      : results
                        ? `${results.length} ${results.length === 1 ? "match" : "matches"} found`
                        : ""}
                </span>
                {searchErr && (
                  <p className="muted" style={{ marginTop: "var(--space-2)" }}>
                    Couldn’t reach search just now — try again, or switch to
                    “Enter id”.
                  </p>
                )}
                {results &&
                  !searching &&
                  results.length === 0 &&
                  !searchErr && (
                    <p className="muted" style={{ marginTop: "var(--space-2)" }}>
                      No matches. Refine the name, or switch to “Enter id”.
                    </p>
                  )}
                {/* First query in flight (no prior results): skeleton rows
                    sketch the list so the panel resolves in place rather than
                    popping in after a blank pause. */}
                {searching && !results && (
                  <ul className="search-results" aria-hidden>
                    {[0, 1, 2].map((i) => (
                      <li className="search-result" key={i}>
                        <div style={{ flex: 1 }}>
                          <span className="skeleton" style={{ width: "45%" }} />
                          <span
                            className="skeleton"
                            style={{
                              width: "28%",
                              marginTop: "var(--space-2)",
                            }}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {results && results.length > 0 && (
                  <ul
                    className={`search-results${searching ? " is-refreshing" : ""}`}
                  >
                    {results.map((h) => {
                      const watchable = isWatchable(h.entity_id);
                      const isSel =
                        selected != null && hitKey(selected) === hitKey(h);
                      return (
                        <li
                          key={hitKey(h)}
                          className={`search-result${isSel ? " selected" : ""}`}
                        >
                          <div>
                            <div>{h.matched_name}</div>
                            <div
                              className="muted mono"
                              style={{ fontSize: 12 }}
                            >
                              {h.entity_id}
                              {h.jurisdiction ? ` · ${h.jurisdiction}` : ""} ·{" "}
                              {Math.round(h.score * 100)}% match
                              {!watchable && " · no event source yet"}
                            </div>
                          </div>
                          <button
                            type="button"
                            className={isSel ? "btn" : "btn secondary"}
                            onClick={() => setSelected(h)}
                          >
                            {isSel ? "Selected" : "Select"}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  gap: "var(--space-3)",
                  flexWrap: "wrap",
                  alignItems: "center",
                  marginTop: "var(--space-3)",
                }}
              >
                <select
                  className="input"
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                >
                  {SOURCES.map(([prefix, label]) => (
                    <option key={prefix} value={prefix}>
                      {label} ({prefix})
                    </option>
                  ))}
                </select>
                <input
                  className="input"
                  placeholder={`Registry number (e.g. ${example})`}
                  value={localId}
                  onChange={(e) => setLocalId(e.target.value)}
                />
                <input
                  className="input"
                  placeholder="Display name (optional)"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
                {companyId && <span className="muted mono">→ {companyId}</span>}
              </div>
            )}

            {companyId && !selectionWatchable && (
              <p className="muted" style={{ marginTop: "var(--space-2)" }}>
                Monitoring covers{" "}
                {REGISTERS.map((r) => `${r.label} (${r.id})`).join(", ")} today —
                other registers have no event source yet.
              </p>
            )}
          </div>

          <div className="form-section">
            <div className="field-label-row">
              <div className="field-label">Events to watch</div>
              {availableEvents.length > 0 && (
                <span className="kind-summary">
                  <span className="kind-group-count">
                    {kinds.length} of {availableEvents.length} selected
                  </span>
                  <button
                    type="button"
                    className="kind-group-toggle"
                    onClick={() => setKindsFor(availableEvents, !allSelected)}
                  >
                    {allSelected ? "Clear all" : "Select all"}
                  </button>
                </span>
              )}
            </div>
            {availableEvents.length === 0 ? (
              <p className="muted">
                Pick a watchable company first — the events you can watch
                depend on its source.
              </p>
            ) : (
              <>
                <div className="kind-groups">
                  {eventGroups.map((g) => {
                    const onCount = g.types.filter((t) =>
                      kinds.includes(t),
                    ).length;
                    const allOn = onCount === g.types.length;
                    return (
                      <div key={g.id}>
                        <div className="kind-group-head">
                          <span className="kind-group-label">{g.label}</span>
                          <span className="kind-group-count">
                            {onCount}/{g.types.length}
                          </span>
                          <button
                            type="button"
                            className="kind-group-toggle"
                            onClick={() => setKindsFor(g.types, !allOn)}
                          >
                            {allOn ? "None" : "All"}
                          </button>
                        </div>
                        <div className="kind-grid">
                          {g.types.map((k) => {
                            const on = kinds.includes(k);
                            return (
                              <label
                                key={k}
                                className={`kind-toggle${on ? " on" : ""}`}
                                title={EVENT_DESCRIPTIONS[k]}
                              >
                                <input
                                  type="checkbox"
                                  checked={on}
                                  onChange={() => toggleKind(k)}
                                />
                                {EVENT_LABELS[k] ?? k}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {kinds.length === 0 && (
                  <p className="muted" style={{ marginTop: "var(--space-2)" }}>
                    Select at least one event to watch.
                  </p>
                )}
                {registerNote && (
                  <p className="muted" style={{ marginTop: "var(--space-2)" }}>
                    {registerNote}
                  </p>
                )}
                <details className="connect-help">
                  <summary>What do these events mean?</summary>
                  <div className="kind-groups">
                    {eventGroups.map((g) => (
                      <div key={g.id}>
                        <div className="kind-group-label">{g.label}</div>
                        <dl className="kind-glossary">
                          {g.types.map((k) => (
                            <div className="kind-glossary-row" key={k}>
                              <dt>{EVENT_LABELS[k] ?? k}</dt>
                              <dd>{EVENT_DESCRIPTIONS[k]}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    ))}
                  </div>
                </details>
              </>
            )}
          </div>

          <div className="form-section">
            <div className="field-label">Notifications</div>
            <label>
              <input
                type="checkbox"
                checked={emailOn}
                disabled={!emailAllowed}
                onChange={(e) => setEmailOn(e.target.checked)}
              />{" "}
              Email me about these events
            </label>
            {!emailAllowed && (
              <span className="muted" style={{ marginLeft: "var(--space-2)" }}>
                (email + alerts resume when you have an active subscription —
                start one from Billing)
              </span>
            )}
            {emailOn && emailAllowed && (
              <div
                style={{
                  display: "flex",
                  gap: "var(--space-3)",
                  flexWrap: "wrap",
                  marginTop: "var(--space-3)",
                }}
              >
                <select
                  className="input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                >
                  {(data?.member_emails ?? []).map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
                <select
                  className="input"
                  value={cadence}
                  onChange={(e) => setCadence(e.target.value as typeof cadence)}
                >
                  <option value="prompt_and_daily">
                    Daily digest + prompt for significant events
                  </option>
                  <option value="daily">Daily digest only</option>
                </select>
              </div>
            )}
          </div>

          <button className="btn primary" disabled={!canSubmit} type="submit">
            {mode === "name" && companyName
              ? `Watch ${companyName}`
              : "Watch company"}
          </button>
        </form>
      </div>

      <h2 style={{ marginTop: "var(--space-6)" }}>Active watchlists</h2>
      {data === null && <p className="muted">Loading…</p>}
      {data && data.watchlists.length === 0 && (
        <p className="muted">Nothing watched yet.</p>
      )}
      {data && data.watchlists.length > 0 && (
        <div className="entity-list">
          {data.watchlists.map((w: Watchlist) => {
            const initial =
              (w.company_name ?? w.company_id).trim().charAt(0).toUpperCase() ||
              "•";
            return (
              <div className="entity-card" key={w.id}>
                <span className="entity-avatar" aria-hidden>
                  {initial}
                </span>
                <div className="entity-body">
                  <div className="entity-name">
                    {w.company_name ?? w.company_id}
                  </div>
                  <div className="entity-meta">
                    <span className="mono">{w.company_id}</span>
                    <span>
                      {w.event_types.length} event
                      {w.event_types.length === 1 ? "" : "s"}
                    </span>
                    <span title={new Date(w.created_at).toLocaleString()}>
                      Added {relativeTime(w.created_at)}
                    </span>
                  </div>
                  <div className="chip-row">
                    <span className="delivery">{deliveryLabel(w)}</span>
                    {w.notify_email && (
                      <span className="muted" style={{ fontSize: 12 }}>
                        {w.notify_email}
                      </span>
                    )}
                  </div>
                </div>
                <div className="entity-actions">
                  <button
                    className="btn secondary"
                    onClick={() => api.revokeWatchlist(w.id).then(refresh)}
                  >
                    Unwatch
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
